/**
 * useSecurityEvents Hook
 * 
 * Custom hook for handling security-related WebSocket events.
 * Manages security alerts for forced entry and propped door detections.
 * 
 * Phase 3C: Security Events Extraction (PR #2)
 * 
 * Events handled:
 * - forcedEntryDetected: Alert when forced entry is detected on a device
 * - proppedDoorDetected: Alert when a door is propped open
 * 
 * @module hooks/useSecurityEvents
 */

import { useCallback } from 'react';
import { useUIStore, useDevicesStore } from '../stores';
import logger from '../utils/logger';

/**
 * Custom hook for handling security-related WebSocket events
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.selectedDeviceIdRef - Ref to currently selected device ID
 * @param {Function} params.showNotification - Function to show toast notifications
 *
 * @returns {Object} Object with security event handler functions
 * @returns {Function} handleForcedEntryDetected - Handler for forcedEntryDetected events
 * @returns {Function} handleProppedDoorDetected - Handler for proppedDoorDetected events
 */
export const useSecurityEvents = ({
  selectedDeviceIdRef,
  showNotification,
}) => {
  // Access Zustand stores (security alerts are in UIStore)
  const {
    setForcedEntryAlert,
    setProppedDoorAlert,
  } = useUIStore();

  /**
   * Handler for forcedEntryDetected events
   * 
   * Shows an alert when forced entry is detected on a device.
   * Only shows alert for the currently selected device.
   * 
   * @param {Object} message - WebSocket message
   */
  const handleForcedEntryDetected = useCallback((message) => {
    const { deviceId, timestamp, message: alertMessage } = message;

    if (deviceId === selectedDeviceIdRef.current) {
      const device = useDevicesStore.getState().devices.find((d) => d.deviceId === deviceId);
      const deviceName = device?.displayName || "Device";
      setForcedEntryAlert({ deviceId, timestamp, message: alertMessage });
      showNotification({ message: `🚨 ${deviceName}: Forced entry detected!`, type: "error" });
      logger.log(`Showing forced entry alert for device ${deviceId}`);
    } else {
      logger.log(`Ignoring forced entry alert for device ${deviceId} (selected: ${selectedDeviceIdRef.current})`);
    }
  }, [selectedDeviceIdRef, setForcedEntryAlert, showNotification]);

  /**
   * Handler for proppedDoorDetected events
   * 
   * Shows an alert when a door is detected as propped open.
   * Only shows alert for the currently selected device.
   * 
   * @param {Object} message - WebSocket message
   */
  const handleProppedDoorDetected = useCallback((message) => {
    const { deviceId, timestamp, message: alertMessage } = message;

    if (deviceId === selectedDeviceIdRef.current) {
      const device = useDevicesStore.getState().devices.find((d) => d.deviceId === deviceId);
      const deviceName = device?.displayName || "Device";
      setProppedDoorAlert({ deviceId, timestamp, message: alertMessage });
      showNotification({ message: `⚠️ ${deviceName}: Door is propped open`, type: "warning" });
      logger.log(`Showing propped door alert for device ${deviceId}`);
    } else {
      logger.log(`Ignoring propped door alert for device ${deviceId} (selected: ${selectedDeviceIdRef.current})`);
    }
  }, [selectedDeviceIdRef, setProppedDoorAlert, showNotification]);

  return {
    handleForcedEntryDetected,
    handleProppedDoorDetected,
  };
};
